import React, { useState, useEffect, useRef, memo } from 'react';
import { useLocation } from 'react-router-dom';
import { useSelector, shallowEqual } from 'react-redux';
import logger from '../utils/logger';

interface PageContainerProps {
  children: React.ReactNode;
  pagePath: string;
  onReactivate?: () => void;
}

/**
 * Prevents children from re-rendering when frozen.
 * Uses shouldComponentUpdate to block React's reconciliation entirely
 * for hidden pages, so they don't waste CPU on Redux-triggered re-renders.
 */
class FreezeOnHidden extends React.Component<{ frozen: boolean; children: React.ReactNode }> {
  shouldComponentUpdate(nextProps: { frozen: boolean }) {
    // When frozen, block ALL re-renders of children
    return !nextProps.frozen;
  }

  render() {
    return this.props.children;
  }
}

const PageContainer: React.FC<PageContainerProps> = ({
  children,
  pagePath,
  onReactivate
}) => {
  const location = useLocation();
  const [hasBeenMounted, setHasBeenMounted] = useState(false);
  const [wasInactive, setWasInactive] = useState(false);
  const [mountTime, setMountTime] = useState<number>(Date.now());

  // Use refs to avoid infinite loops
  const lastAuthCheck = useRef<string>('');
  const safetyIntervalRef = useRef<NodeJS.Timeout | null>(null);

  // Get auth state from Redux with shallowEqual to prevent unnecessary re-renders
  const authUser = useSelector(
    (state: any) => state?.rootReducers?.auth?.user,
    shallowEqual
  );
  const authToken = localStorage.getItem("auth_token");

  // EXACT route matching
  const isActive = location.pathname === pagePath;

  // MAIN EFFECT - Only depend on route changes
  useEffect(() => {
    if (isActive && !hasBeenMounted) {
      logger.log(`🎯 MOUNTING PAGE: ${pagePath} for the first time`);
      setHasBeenMounted(true);
      setMountTime(Date.now());
    }

    if (isActive) {
      logger.log(`👀 PAGE ACTIVATED: ${pagePath}`);

      if (wasInactive && onReactivate) {
        onReactivate();
        setWasInactive(false);
      }
    } else if (hasBeenMounted) {
      setWasInactive(true);
    }
  }, [isActive, hasBeenMounted, pagePath, wasInactive, onReactivate]);

  // AUTH CLEANUP - Only check for the ACTIVE page
  useEffect(() => {
    if (!isActive) return;

    const checkAuth = () => {
      const currentAuthState = `${!!authUser}-${!!authToken}`;

      if (currentAuthState !== lastAuthCheck.current) {
        lastAuthCheck.current = currentAuthState;

        const isAuthenticated = !!(authUser && authToken);

        if (!isAuthenticated && hasBeenMounted) {
          setHasBeenMounted(false);
          setWasInactive(false);
          setMountTime(Date.now());
        }
      }
    };

    checkAuth();
  }, [isActive, location.pathname]);

  // 12-HOUR SAFETY REMOUNT
  useEffect(() => {
    if (hasBeenMounted) {
      if (safetyIntervalRef.current) {
        clearInterval(safetyIntervalRef.current);
      }

      safetyIntervalRef.current = setInterval(() => {
        const timeSinceMounted = (Date.now() - mountTime) / (1000 * 60);

        if (timeSinceMounted >= 720) {
          setHasBeenMounted(false);
          if (safetyIntervalRef.current) {
            clearInterval(safetyIntervalRef.current);
            safetyIntervalRef.current = null;
          }
        }
      }, 30 * 60 * 1000);

      return () => {
        if (safetyIntervalRef.current) {
          clearInterval(safetyIntervalRef.current);
          safetyIntervalRef.current = null;
        }
      };
    }
  }, [hasBeenMounted, mountTime, pagePath]);

  // Don't render until first visit
  if (!hasBeenMounted) {
    return null;
  }

  return (
    <div
      style={{
        display: isActive ? 'block' : 'none',
        height: '100vh',
        width: '100%'
      }}
      data-page={pagePath}
      data-active={isActive}
    >
      <FreezeOnHidden frozen={!isActive}>
        {children}
      </FreezeOnHidden>
    </div>
  );
};

export default memo(PageContainer);
